/**
 * Portuguese Cartão de Cidadão (Citizen Card) ID number validation utility
 * 
 * The Portuguese Citizen Card ID number validation follows the official algorithm
 * as specified in the AMA document "Validação de Número de Documento do Cartão de Cidadão v1.0"
 * 
 * Format: DDDDDDDD C AAT (12 characters total)
 * Where:
 * - D: Civil Identification Number [0-9] (9 digits)
 * - C: Check Digit of Civil Identification Number [0-9] (1 digit)
 * - A: Version [A-Z, 0-9] (2 alphanumeric characters)
 * - T: Document Number Check Digit [0-9] (1 digit)
 */

/**
 * Converts a character to its numeric value according to the official conversion table
 * @param {string} char - The character to convert
 * @returns {number} - The numeric value of the character
 */
const getNumberFromChar = (char) => {
  switch (char.toUpperCase()) {
    case '0': return 0;
    case '1': return 1;
    case '2': return 2;
    case '3': return 3;
    case '4': return 4;
    case '5': return 5;
    case '6': return 6;
    case '7': return 7;
    case '8': return 8;
    case '9': return 9;
    case 'A': return 10;
    case 'B': return 11;
    case 'C': return 12;
    case 'D': return 13;
    case 'E': return 14;
    case 'F': return 15;
    case 'G': return 16;
    case 'H': return 17;
    case 'I': return 18;
    case 'J': return 19;
    case 'K': return 20;
    case 'L': return 21;
    case 'M': return 22;
    case 'N': return 23;
    case 'O': return 24;
    case 'P': return 25;
    case 'Q': return 26;
    case 'R': return 27;
    case 'S': return 28;
    case 'T': return 29;
    case 'U': return 30;
    case 'V': return 31;
    case 'W': return 32;
    case 'X': return 33;
    case 'Y': return 34;
    case 'Z': return 35;
    default:
      throw new Error(`Invalid character in document number: ${char}`);
  }
};

/**
 * Validates a Portuguese Cartão de Cidadão ID number using the official algorithm
 * @param {string} numeroDocumento - The document number to validate (can contain spaces)
 * @returns {boolean} - True if the document number is valid, false otherwise
 */
export const validateIdNumber = (numeroDocumento) => {
  if (!numeroDocumento || typeof numeroDocumento !== 'string') {
    return false;
  }

  // Remove spaces from the document number
  const cleanDocumento = numeroDocumento.replace(/\s/g, '');
  
  // Document number must be exactly 12 characters
  if (cleanDocumento.length !== 12) {
    return false;
  }

  try {
    let sum = 0;
    let secondDigit = false;

    // Process from right to left (as per official algorithm)
    for (let i = cleanDocumento.length - 1; i >= 0; i--) {
      let valor = getNumberFromChar(cleanDocumento[i]);
      
      // Double every second digit (counting from right)
      if (secondDigit) {
        valor *= 2;
        // If result >= 10, subtract 9
        if (valor > 9) {
          valor -= 9;
        }
      }
      
      sum += valor;
      secondDigit = !secondDigit;
    }

    // Valid if sum modulo 10 equals 0
    return (sum % 10) === 0;
  } catch (error) {
    // Invalid character found
    return false;
  }
};

/**
 * Formats a document number for display (adds spaces for readability)
 * Format: DDDDDDDD C AAT
 * @param {string} numeroDocumento - The document number to format
 * @returns {string} - Formatted document number (DDDDDDDD C AAT)
 */
export const formatIdNumber = (numeroDocumento) => {
  if (!numeroDocumento || typeof numeroDocumento !== 'string') {
    return '';
  }

  const cleanId = numeroDocumento.replace(/\s/g, '').toUpperCase();
  
  if (cleanId.length === 12) {
    // Format as: DDDDDDDD C AAT
    return `${cleanId.slice(0, 8)} ${cleanId.slice(8, 9)} ${cleanId.slice(9, 12)}`;
  }
  
  return cleanId;
};

/**
 * Cleans a document number by removing spaces and converting to uppercase
 * @param {string} numeroDocumento - The document number to clean
 * @returns {string} - Cleaned document number (12 characters, uppercase)
 */
export const cleanIdNumber = (numeroDocumento) => {
  if (!numeroDocumento || typeof numeroDocumento !== 'string') {
    return '';
  }

  return numeroDocumento.replace(/\s/g, '').toUpperCase();
};

/**
 * Validates the format of a document number without checking the algorithm
 * @param {string} numeroDocumento - The document number to validate format
 * @returns {boolean} - True if format is correct (12 characters), false otherwise
 */
export const isValidIdNumberFormat = (numeroDocumento) => {
  if (!numeroDocumento || typeof numeroDocumento !== 'string') {
    return false;
  }

  const cleanId = cleanIdNumber(numeroDocumento);
  
  // Must be exactly 12 characters
  if (cleanId.length !== 12) {
    return false;
  }
  
  // Check format: 8 digits + 1 digit + 2 alphanumeric + 1 digit
  // DDDDDDDD C AAT
  const formatRegex = /^[0-9]{9}[A-Z0-9]{2}[0-9]$/;
  return formatRegex.test(cleanId);
};
